/* eslint-disable @typescript-eslint/strict-boolean-expressions */
// @ts-nocheck
import { Box, Button, FormControlLabel, Stack, Switch } from '@mui/material';
import PnLChart from 'components/optionsCalculator/PnLChart';
import PositionContainer from 'components/optionsCalculator/positionManagement/PositionContainer';
import TickerOverview from 'components/optionsCalculator/TickerOverview';
import { ChartSizingButton } from 'components/shared/ChartSizingButton';
import { COMBINED_SIGNALS } from 'config';
import { useOptionsCalculatorActions } from 'hooks/optionsCalculator/useOptionsCalculatorActions';
import { useEffect, useCallback, useMemo, useRef, useState } from 'react';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import {
  searchSuggestionsData,
  searchHandlerState,
  hiroSymbolsState,
} from 'states';
import { EquityFieldKey, GREEK_IDX, OPTION_IDX } from 'types';
import { SearchHandlerFunction, SuggestionSearchData } from 'types/shared';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import { useOptionsCalculatorData } from 'hooks/optionsCalculator/useOptionsCalculatorData';
import {
  NEW_POSITION_ID,
  OC_SAVED_POSITIONS_CACHE_KEY,
  Position as PositionType,
  OptionsCalculatorDraftState,
  OCSettingsItem,
  OC_DEFAULT_SYM,
  StrikeDeltaObject,
  PositionLeg,
} from 'types/optionsCalculator';
import {
  generateDefaultOptionLeg,
  getUpdatedLeg,
} from 'util/optionsCalculator';
import { useQuery } from '@tanstack/react-query';
import usePrices from 'hooks/equityhub/usePrices';
import useImpliedVolatility from 'hooks/iVol/useImpliedVolatility';
import StrategiesContainer from 'components/optionsCalculator/Strategies/StrategiesContainer';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import ShareIcon from '@mui/icons-material/Share';
import ZoomOut from '@mui/icons-material/ZoomOut';
import { optionsCalculatorShowKeyLevelsState } from 'states/optionsCalculator';
import CheckIcon from '@mui/icons-material/Check';
import AddIcon from '@mui/icons-material/Add';
import { useOptionsCalculatorComputed } from 'hooks/optionsCalculator/useOptionsCalculatorComputed';
import { DownloadChartButton } from 'components/shared/DownloadChartButton';
import { SGTooltip } from 'components/core';
import { InfoButton } from 'components/shared';
import { getVolFromTableData } from 'util/riskEngine';
import { useDividends } from 'hooks/useDividends';
import { transformDividendsToRiskEngineFormat } from 'util/dividendUtils';

const isValidSymbol = (sym: string) => {
  return !COMBINED_SIGNALS.has(sym);
};

const OptionsCalculatorContainer = () => {
  const [tab, setTab] = useState<'new' | 'saved'>('new');
  const hiroSyms = useRecoilValue(hiroSymbolsState);
  const [chartFullscreen, setChartFullscreen] = useState<boolean>(false);
  const [showKeyLevels, setShowKeyLevels] = useRecoilState(
    optionsCalculatorShowKeyLevelsState,
  );

  const [forceDefaultZoom, setForceDefaultZoom] = useState<boolean>(true);
  const [copied, setCopied] = useState<boolean>(false);

  const setSuggestionsData = useSetRecoilState(searchSuggestionsData);
  const setSuggestionsHandler = useSetRecoilState(searchHandlerState);
  const { getCurrentGreeksData } = useImpliedVolatility({
    bypassAccessCheck: true,
  });
  const { getPrices } = usePrices({ bypassAccessCheck: true });
  const hasInitializedRef = useRef<boolean>(false);

  const {
    setActivePosition,
    getSavedPositions,
    updateBaseDate,
    getRiskFreeRate,
  } = useOptionsCalculatorActions();
  const {
    sym,
    legs,
    shares,
    ivSettings,
    allGreeksData,
    allPrices,
    currentKeyLevels,
    allEquities,
    allVolDataCall,
    allVolDataPut,
    isLoading: isPnlLoading,
  } = useOptionsCalculatorData();
  const { pnlChartRef } = useOptionsCalculatorComputed();

  // Centralized draft state
  const [draftState, setDraftState] = useState<OptionsCalculatorDraftState>({
    newPosition: {
      id: NEW_POSITION_ID,
      option_legs: legs,
      underlying_shares: shares,
      iv_adjustments: ivSettings,
      sym: sym,
    },
    savedPositionDrafts: new Map(),
    activePositionId: NEW_POSITION_ID,
    hasUnsavedChanges: new Map(),
  });

  // Query for saved positions
  const { data: savedPositions, isFetching: isFetchingSavedPositions } =
    useQuery({
      queryKey: [OC_SAVED_POSITIONS_CACHE_KEY],
      queryFn: getSavedPositions,
      refetchOnWindowFocus: false,
      staleTime: Infinity,
      gcTime: Infinity,
    });

  const { data: dividendsData } = useDividends({ sym });
  const { dividendDates, dividendAmounts } = useMemo(
    () => transformDividendsToRiskEngineFormat(dividendsData ?? []),
    [dividendsData],
  );

  // Initialize search suggestions
  useEffect(() => {
    const suggestionsHandler: SearchHandlerFunction = async (
      val: string,
    ): Promise<void> => {
      if (val != null && val != '') {
        const [newGreeks, newPricesPayload] = await Promise.all([
          getCurrentGreeksData(val),
          getPrices([val]),
        ]);

        const defaultLeg = generateDefaultOptionLeg(
          val,
          getRiskFreeRate,
          newGreeks ?? undefined,
          newPricesPayload?.get(val) ?? undefined,
        );

        if (defaultLeg) {
          // Reset to new position and overwrite completely with default leg
          const defaultNewPosition = {
            id: NEW_POSITION_ID,
            option_legs: defaultLeg ? [defaultLeg] : [],
            underlying_shares: [],
            iv_adjustments: [],
            sym: val,
          };
          updatePosition(NEW_POSITION_ID, defaultNewPosition, true);
          updateBaseDate(defaultLeg.expiry);
          setTab('new'); // Switch to new position tab
        }
      }
    };
    setSuggestionsHandler(() => suggestionsHandler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setSuggestionsHandler]);

  useEffect(() => {
    const result: SuggestionSearchData[] = Array.from(hiroSyms.values()).map(
      (data: string) => ({
        symbol: data,
        name: allEquities?.get(data)?.[EquityFieldKey.name] || data,
      }),
    );

    const suggestions = result.filter(
      (value: { symbol: string; name: string }) => isValidSymbol(value.symbol),
    );
    setSuggestionsData(suggestions);
  }, [setSuggestionsData, hiroSyms, allEquities]);

  // Initialize new position with default leg when symbol/data is available
  useEffect(() => {
    if (!hasInitializedRef.current && sym && allGreeksData && allPrices) {
      // If the page already has legs or shares, mark as initialized to prevent reset
      if (
        draftState.newPosition.option_legs.length > 0 ||
        draftState.newPosition.underlying_shares.length > 0
      ) {
        hasInitializedRef.current = true;
        return;
      }

      // Only create default leg if position is empty
      if (
        draftState.newPosition.option_legs.length === 0 &&
        draftState.newPosition.underlying_shares.length === 0
      ) {
        const symToUse = isValidSymbol(sym) ? sym : OC_DEFAULT_SYM;
        const defaultLeg = generateDefaultOptionLeg(
          symToUse,
          getRiskFreeRate,
          allGreeksData.get(symToUse),
          allPrices.get(symToUse),
        );
        if (defaultLeg) {
          updatePosition(
            NEW_POSITION_ID,
            {
              option_legs: [defaultLeg],
              underlying_shares: [],
              iv_adjustments: [],
              sym: symToUse,
            },
            true,
          );
          updateBaseDate(defaultLeg.expiry);
          hasInitializedRef.current = true;
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    sym,
    allGreeksData,
    allPrices,
    draftState.newPosition.option_legs.length,
    draftState.newPosition.underlying_shares.length,
  ]);

  // Initialize saved position drafts when saved positions are loaded
  // Preserve existing drafts to prevent losing unsaved changes
  useEffect(() => {
    if (savedPositions && savedPositions.length > 0) {
      setDraftState((prev) => {
        const newDrafts = new Map(prev.savedPositionDrafts);

        // Only update positions that don't already have drafts
        // This preserves existing draft changes when data refetches
        savedPositions.forEach((position) => {
          if (!newDrafts.has(position.id)) {
            newDrafts.set(position.id, { ...position });
          } else {
            // Position already has a draft - only update if it doesn't have unsaved changes
            const hasUnsavedChanges = prev.hasUnsavedChanges.get(position.id);
            if (!hasUnsavedChanges) {
              newDrafts.set(position.id, { ...position });
            }
            // If it has unsaved changes, keep the existing draft intact
          }
        });

        return {
          ...prev,
          savedPositionDrafts: newDrafts,
        };
      });
    }
  }, [savedPositions]);

  // Get the currently active position (either new or saved draft)
  const activePosition = useMemo((): PositionType => {
    if (draftState.activePositionId === NEW_POSITION_ID) {
      return draftState.newPosition;
    }

    const savedDraft = draftState.savedPositionDrafts.get(
      draftState.activePositionId,
    );
    if (savedDraft) {
      return savedDraft;
    }

    // Fallback to global state (shouldn't happen in normal flow)
    return {
      id: draftState.activePositionId,
      option_legs: legs,
      underlying_shares: shares,
      iv_adjustments: ivSettings,
      sym: sym,
    };
  }, [draftState, legs, shares, ivSettings, sym]);

  // Handler for switching active position - makes global state updates explicit
  const handleSetActivePosition = useCallback(
    (positionId: number = NEW_POSITION_ID, position?: PositionType) => {
      // Update local draft state
      setDraftState((prev) => ({
        ...prev,
        activePositionId: positionId,
      }));

      // Determine the position to activate
      let positionToActivate: PositionType;
      if (positionId === NEW_POSITION_ID) {
        positionToActivate = position ?? draftState.newPosition;
      } else {
        const updatedPosition =
          position ?? draftState.savedPositionDrafts.get(positionId);
        if (updatedPosition == null) {
          return;
        }
        positionToActivate = updatedPosition;
      }

      setActivePosition(positionToActivate);
    },
    [draftState.newPosition, draftState.savedPositionDrafts, setActivePosition],
  );

  // Unified position update function (works for both new and saved positions)
  const updatePosition = useCallback(
    (
      positionId: number,
      updates: Partial<PositionType>,
      overrideActivePosition: boolean = false,
    ) => {
      setDraftState((prev) => {
        const updatedState = { ...prev };

        // Update the appropriate position
        if (positionId === NEW_POSITION_ID) {
          updatedState.newPosition = overrideActivePosition
            ? ({ ...updates, id: NEW_POSITION_ID } as PositionType)
            : { ...prev.newPosition, ...updates, id: NEW_POSITION_ID };
        } else {
          const currentDraft = prev.savedPositionDrafts.get(positionId);
          if (currentDraft) {
            const updatedDraft = overrideActivePosition
              ? ({ ...updates } as PositionType)
              : { ...currentDraft, ...updates };
            const newDrafts = new Map(prev.savedPositionDrafts);
            newDrafts.set(positionId, updatedDraft);
            updatedState.savedPositionDrafts = newDrafts;
          }
        }

        // Mark as having unsaved changes (if it applies)
        // Check if the updated position matches the original saved position
        let hasChanges = true;
        if (positionId !== NEW_POSITION_ID) {
          const originalPosition = savedPositions?.find(
            (p) => p.id === positionId,
          );
          if (originalPosition != null) {
            const draft = updatedState.savedPositionDrafts.get(positionId);

            if (draft != null) {
              // Compare relevant fields to determine if there are actual changes
              hasChanges =
                draft.sym !== originalPosition.sym ||
                JSON.stringify(draft.option_legs) !==
                  JSON.stringify(originalPosition.option_legs) ||
                JSON.stringify(draft.underlying_shares) !==
                  JSON.stringify(originalPosition.underlying_shares) ||
                JSON.stringify(draft.iv_adjustments) !==
                  JSON.stringify(originalPosition.iv_adjustments);
            }
          }
        }

        updatedState.hasUnsavedChanges = new Map(prev.hasUnsavedChanges).set(
          positionId,
          hasChanges,
        );

        return updatedState;
      });

      // If updating the currently active position, sync with global state
      if (
        positionId === draftState.activePositionId ||
        overrideActivePosition
      ) {
        const updatedPosition = overrideActivePosition
          ? ({ ...updates } as PositionType)
          : positionId === NEW_POSITION_ID
            ? { ...draftState.newPosition, ...updates }
            : {
                ...draftState.savedPositionDrafts.get(positionId)!,
                ...updates,
              };

        handleSetActivePosition(positionId, updatedPosition);
      }
    },
    [draftState, savedPositions, handleSetActivePosition],
  );

  // Reset unsaved changes flag (called after successful save)
  const markPositionAsSaved = (positionId: number) => {
    setDraftState((prev) => {
      const newHasUnsavedChanges = new Map(prev.hasUnsavedChanges);
      newHasUnsavedChanges.set(positionId, false);
      return {
        ...prev,
        hasUnsavedChanges: newHasUnsavedChanges,
      };
    });
  };

  // Handle strike bar changes from PnLChart
  const handleLegChange = (
    legId: number,
    updates: Partial<PositionLeg>,
    shouldUseDefaultPrice = false,
  ) => {
    const currentLeg = activePosition.option_legs?.find(
      (leg) => leg.leg_id === legId,
    );

    if (!currentLeg || !allGreeksData?.get(sym)) {
      return;
    }

    // Use getUpdatedLeg utility function to handle the update logic
    const updatedLeg = getUpdatedLeg(
      currentLeg,
      updates,
      shouldUseDefaultPrice,
      allGreeksData.get(sym)!,
    );

    const updatedLegs = activePosition.option_legs?.map((leg) =>
      leg.leg_id === legId ? updatedLeg : leg,
    );

    updatePosition(draftState.activePositionId, {
      option_legs: updatedLegs ?? [],
    });
  };

  // Remove a saved position draft (called after deletion)
  const removeSavedPositionDraft = (positionId: number) => {
    setDraftState((prev) => {
      const newDrafts = new Map(prev.savedPositionDrafts);
      newDrafts.delete(positionId);

      const newHasUnsavedChanges = new Map(prev.hasUnsavedChanges);
      newHasUnsavedChanges.delete(positionId);

      // If the deleted position was active, switch to new position
      const newActivePositionId =
        prev.activePositionId === positionId
          ? NEW_POSITION_ID
          : prev.activePositionId;

      return {
        ...prev,
        savedPositionDrafts: newDrafts,
        hasUnsavedChanges: newHasUnsavedChanges,
        activePositionId: newActivePositionId,
      };
    });

    // If we switched to new position due to deletion, update global state
    if (positionId === draftState.activePositionId) {
      setActivePosition(draftState.newPosition);
    }
  };

  // Add new saved position draft (called after successful save of new position)
  const addSavedPositionDraft = (position: PositionType) => {
    setDraftState((prev) => {
      const newDrafts = new Map(prev.savedPositionDrafts);
      newDrafts.set(position.id, { ...position });

      return {
        ...prev,
        savedPositionDrafts: newDrafts,
        activePositionId: position.id, // Switch to the newly saved position
        hasUnsavedChanges: new Map(prev.hasUnsavedChanges).set(
          position.id,
          false,
        ),
      };
    });

    // Update global state to reflect the newly saved and active position
    setActivePosition(position);
  };

  const strikeDataPerExpiration: Map<number, StrikeDeltaObject[]> =
    useMemo(() => {
      const greeksData = allGreeksData?.get(sym);
      if (!greeksData) {
        return new Map<number, StrikeDeltaObject[]>();
      }

      const strikesMap = new Map<number, StrikeDeltaObject[]>();

      // Get all available expirations from greeks data
      Object.keys(greeksData).forEach((expiration: string) => {
        const greeks = greeksData[Number(expiration)];
        if (greeks) {
          const strikeDeltas = Object.keys(greeks)
            .map((strike: string) => {
              const strikeNum = Number(strike);
              const callGreeks = greeks[strikeNum]?.[OPTION_IDX.CALL];
              const putGreeks = greeks[strikeNum]?.[OPTION_IDX.PUT];
              const callDelta = callGreeks?.[GREEK_IDX.DELTA];
              const putDelta = putGreeks?.[GREEK_IDX.DELTA];
              return { strike: strikeNum, callDelta, putDelta };
            })
            .sort((a, b) => a.strike - b.strike);
          strikesMap.set(Number(expiration), strikeDeltas);
        }
      });

      return strikesMap;
    }, [allGreeksData, sym]);

  const addNewLegToActivePosition = useCallback(
    (optType: 'C' | 'P') => {
      const newLeg = generateDefaultOptionLeg(
        activePosition.sym,
        getRiskFreeRate,
        allGreeksData?.get(activePosition.sym),
        allPrices?.get(activePosition.sym),
      );

      if (newLeg == null) {
        return;
      }

      if (newLeg != null && activePosition.option_legs != null) {
        newLeg.expiry = activePosition.option_legs.reduce(
          (acc, l) => Math.min(acc, l.expiry),
          Infinity,
        );
      }

      updatePosition(activePosition.id, {
        option_legs: [
          ...(activePosition.option_legs ?? []),
          {
            ...newLeg,
            option_type: optType,
            position_id: activePosition.id,
          },
        ],
      });
    },
    // TODO: updatePosition could be converted to a useCallback itself to avoid deps lint
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activePosition, allGreeksData, allPrices],
  );

  const onDeleteLeg = useCallback(
    (legId: number) => {
      const updatedLegs = activePosition.option_legs?.filter(
        (leg) => leg.leg_id !== legId,
      );
      updatePosition(activePosition.id, { option_legs: updatedLegs ?? [] });
    },
    [updatePosition, activePosition.option_legs, activePosition.id],
  );

  const legIVs = new Map(
    isPnlLoading
      ? []
      : (activePosition.option_legs
          .map((leg) => {
            const iv = getVolFromTableData(
              leg.option_type === 'C'
                ? allVolDataCall.get(sym)!
                : allVolDataPut.get(sym)!,
              leg.strike,
              leg.expiry,
            );

            return [leg.leg_id, iv] as const;
          })
          .filter(([_, iv]) => iv != null) as [number, number][]),
  );

  const hasLegs = legs.length > 0;
  const hasShares = shares.length > 0;
  const hasPosition = hasLegs || hasShares;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
        height: chartFullscreen ? '100%' : 'auto',
      }}
    >
      <Box
        sx={{
          gap: 2,
          display: chartFullscreen ? 'none' : 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          width: '100%',
        }}
      >
        <Stack
          sx={{
            position: 'relative',
            bgcolor: 'background.paper',
            borderRadius: 2,
            p: '12px',
            maxWidth: { xs: '100%', md: '50%' },
            minWidth: { xs: '100%', md: '350px' },
          }}
        >
          <TickerOverview />
        </Stack>

        <Stack
          sx={{
            flex: 1,
            position: 'relative',
            bgcolor: 'background.paper',
            borderRadius: 2,
            p: '12px',
          }}
        >
          <StrategiesContainer
            onUpdateNewPosition={(updates: Partial<PositionType>) => {
              updatePosition(NEW_POSITION_ID, updates, true);
              setDraftState((prev) => ({
                ...prev,
                activePositionId: NEW_POSITION_ID,
              }));
            }}
            greeksData={allGreeksData?.get(sym)}
            currentPrice={allPrices?.get(sym)}
            sym={sym}
            setTab={setTab}
            setBaseDate={updateBaseDate}
            setForceDefaultZoom={setForceDefaultZoom}
            dividendDates={dividendDates}
            dividendAmounts={dividendAmounts}
          />
        </Stack>
      </Box>
      <Box
        sx={{
          bgcolor: 'background.paper',
          borderRadius: 2,
          p: '12px',
          minHeight: '350px',
          height: chartFullscreen ? '100%' : 'auto',
        }}
      >
        {hasPosition && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 2,
              mb: 3,
            }}
          >
            <Stack direction="row" spacing={2}>
              <SGTooltip title="Add an at-the-money call to the PnL chart, defaulting to the nearest expiration within the position">
                <Button
                  startIcon={<AddIcon />}
                  onClick={() => addNewLegToActivePosition('C')}
                  sx={{ fontSize: '14px', textTransform: 'none' }}
                >
                  Add Call
                </Button>
              </SGTooltip>
              <SGTooltip title="Add an at-the-money put to the PnL chart, defaulting to the nearest expiration within the position">
                <Button
                  startIcon={<AddIcon />}
                  onClick={() => addNewLegToActivePosition('P')}
                  sx={{ fontSize: '14px', textTransform: 'none' }}
                >
                  Add Put
                </Button>
              </SGTooltip>
            </Stack>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: { xs: 2, md: 3 },
                flexWrap: 'wrap',
              }}
            >
              <SGTooltip title="Reset the PnL chart to the default view for that options, showing a clear view of the position's value across strikes">
                <Button
                  size="small"
                  color="primary"
                  onClick={() => setForceDefaultZoom(true)}
                  sx={{
                    textTransform: 'none',
                    fontSize: { xs: '11px', sm: '12px' },
                    fontWeight: 500,
                  }}
                  startIcon={<ZoomOut fontSize="small" />}
                  id={OCSettingsItem.RESTORE_DEFAULT_VIEW}
                >
                  Restore Default View
                </Button>
              </SGTooltip>

              <SGTooltip title="Copy the URL to share your PnL chart with others, who can paste the link to see the same chart">
                <Button
                  onClick={() => {
                    const url = new URL(window.location.href);
                    void navigator.clipboard.writeText(url.toString());
                    setCopied(true);
                    setTimeout(() => {
                      setCopied(false);
                    }, 3_000);
                  }}
                  size="small"
                  id="share-url"
                  startIcon={
                    copied ? (
                      <CheckIcon fontSize="small" />
                    ) : (
                      <ShareIcon fontSize="small" />
                    )
                  }
                  color="primary"
                  sx={{
                    textTransform: 'none',
                    fontSize: { xs: '11px', sm: '12px' },
                    fontWeight: 500,
                  }}
                >
                  {copied ? (
                    <>
                      <Box sx={{ display: { xs: 'none', sm: 'inline' } }}>
                        Copied to clipboard!
                      </Box>
                      <Box sx={{ display: { xs: 'inline', sm: 'none' } }}>
                        Copied!
                      </Box>
                    </>
                  ) : (
                    'Share'
                  )}
                </Button>
              </SGTooltip>

              {currentKeyLevels && currentKeyLevels.length > 0 && (
                <SGTooltip title="Overlay SpotGamma key levels for the selected ticker directly onto the PnL chart">
                  <FormControlLabel
                    color="primary"
                    control={
                      <Switch
                        size="small"
                        checked={showKeyLevels}
                        onChange={(
                          event: React.ChangeEvent<HTMLInputElement>,
                        ) => setShowKeyLevels(event.target.checked)}
                        id={OCSettingsItem.KEY_LEVELS}
                      />
                    }
                    label={
                      <>
                        <Box sx={{ display: { xs: 'none', sm: 'inline' } }}>
                          Key Levels
                        </Box>
                        <Box sx={{ display: { xs: 'inline', sm: 'none' } }}>
                          Levels
                        </Box>
                      </>
                    }
                    labelPlacement="end"
                    sx={{
                      '& .MuiFormControlLabel-label': {
                        color: 'primary.main',
                        fontSize: { xs: '11px', sm: '14px' },
                      },
                      mr: 1,
                    }}
                  />
                </SGTooltip>
              )}

              <DownloadChartButton
                key="pnl-download"
                chartRef={pnlChartRef}
                selectedSym={sym}
                chartName="PnL"
                description="Download a snapshot of the current PnL chart"
              />

              <ChartSizingButton
                onClick={() => setChartFullscreen((prev) => !prev)}
                icon={
                  chartFullscreen ? (
                    <FullscreenExitIcon fontSize="small" color="primary" />
                  ) : (
                    <FullscreenIcon fontSize="small" color="primary" />
                  )
                }
                title="Expand the PnL chart to occupy the full window"
                id={OCSettingsItem.FULLSCREEN}
              />
              <InfoButton key={`pnl-info`} articleKey="options-calculator" />
            </Box>
          </Box>
        )}
        <PnLChart
          showKeyLevels={showKeyLevels}
          forceDefaultZoom={forceDefaultZoom}
          setForceDefaultZoom={setForceDefaultZoom}
          isFullscreen={chartFullscreen}
          onLegChange={handleLegChange}
          onLegDelete={onDeleteLeg}
          strikeDataPerExpiration={strikeDataPerExpiration}
          legIVs={legIVs}
        />
      </Box>
      <Box
        sx={{
          bgcolor: 'background.paper',
          borderRadius: 2,
          p: '12px',
          display: chartFullscreen ? 'none' : 'inherit',
          mb: '72px',
        }}
      >
        <PositionContainer
          tab={tab}
          setTab={setTab}
          greeksLoading={isPnlLoading}
          // Pass down all draft state and handlers
          activePositionId={draftState.activePositionId}
          onSetActivePositionId={handleSetActivePosition}
          // New position draft state
          newPosition={draftState.newPosition}
          onUpdateNewPosition={(updates: Partial<PositionType>) =>
            updatePosition(NEW_POSITION_ID, updates)
          }
          // Saved positions draft state
          savedPositionDrafts={draftState.savedPositionDrafts}
          onUpdateSavedPositionDraft={(
            positionId: number,
            updates: Partial<PositionType>,
          ) => updatePosition(positionId, updates)}
          onRemoveSavedPositionDraft={removeSavedPositionDraft}
          onAddSavedPositionDraft={addSavedPositionDraft}
          // Change tracking
          hasUnsavedChanges={draftState.hasUnsavedChanges}
          onMarkPositionAsSaved={markPositionAsSaved}
          // Saved positions data
          savedPositions={savedPositions ?? []}
          isFetchingSavedPositions={isFetchingSavedPositions}
          setForceDefaultZoom={setForceDefaultZoom}
          strikeDataPerExpiration={strikeDataPerExpiration}
          legIVs={legIVs}
        />
      </Box>
    </Box>
  );
};

export default OptionsCalculatorContainer;
